import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { AdminWallet } from "../api/types.js";
import { count, trx, utc } from "../format.js";
import { Address, Blank, ErrorNote, Note, Panel, Pill, ScreenHeader, Spinner, Table, Td, Th, Tile, TxId } from "../ui.js";

/**
 * Our own addresses, read from the chain (docs/product/mocks/admin-wallets-providers-mock.html).
 * `GET /v1/admin/wallets` (viewer). Addresses only; the API holds no key.
 */
const ROLE_NOTE: Record<AdminWallet["role"], string> = {
  activator: "opens new wallets",
  payment: "takes direct-transfer orders",
  collector: "sweep target, pays provider top-ups",
  fund: "fund wallet, not counted",
};

const STATUS_TONE = { ok: "good", low: "attention", empty: "bad", unknown: "neutral" } as const;

function Meter({ available, limit }: { available: number | null; limit: number | null }) {
  if (available === null || limit === null) return <Blank title="Not read" />;
  const pct = limit > 0 ? Math.min(100, Math.round((available / limit) * 100)) : 0;
  return (
    <span class="num">
      {count(available)} / {count(limit)}
      <span class="mt-1 block h-1 w-[110px] overflow-hidden rounded bg-surface-raised">
        <i class="block h-full bg-accent" style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}

export function WalletsScreen() {
  const wallets = useResource((signal) => {
    void signal;
    return adminReads.wallets();
  }, []);
  const view = wallets.data;
  const summary = view?.summary;
  const usdt = summary === undefined ? "" : summary.wallets_usdt.toFixed(2);

  return (
    <>
      <ScreenHeader
        title="Wallets"
        lead="Our own addresses, read from the chain. Low and empty marks use each wallet's own threshold."
        action={view === undefined || view === null ? null : <span class="num text-[11px] text-ink-muted">read {utc(view.as_of)}</span>}
      />
      {wallets.loading && view == null ? (
        <Spinner />
      ) : wallets.error !== null ? (
        <ErrorNote error={wallets.error} onRetry={() => wallets.reload()} />
      ) : (
        <>
          <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Tile
              label="Held for customers"
              value={summary === undefined ? null : trx(summary.customers_sun, 1)}
              note="Sum of customer balances. This money is not ours to spend."
            />
            <Tile
              label="On our wallets"
              value={summary === undefined ? null : trx(summary.wallets_sun, 1)}
              note={`Activator, payment, collector and fund wallets, plus ${usdt} USDT.${summary?.complete === false ? " A wallet could not be read: lower bound." : ""}`}
            />
            <Tile
              label="Free"
              value={summary === undefined ? null : trx(summary.free_sun, 1)}
              tone={summary !== undefined && summary.free_sun < 0 ? "danger" : "good"}
              note="On our wallets minus held for customers."
            />
            <Tile
              label="Collector can spend"
              value={summary?.collector_free_sun == null ? null : trx(summary.collector_free_sun, 1)}
              tone={summary?.collector_free_sun != null && summary.collector_free_sun < 0 ? "danger" : "good"}
              note={`Collector ${summary?.collector_sun == null ? "—" : trx(summary.collector_sun, 1)} minus held for customers: safe for provider top-ups.`}
            />
          </div>
          <Panel title="Our addresses" subtitle="Energy and bandwidth: available now / daily limit.">
            <Table
              rows={view?.data.length ?? 0}
              empty="No wallet is configured: set ACTIVATOR_ADDRESS, PAYMENT_ADDRESS_* or PAYMENT_SWEEP_TO on the API."
              head={
                <tr>
                  <Th>Role</Th>
                  <Th>Address</Th>
                  <Th numeric>TRX</Th>
                  <Th numeric>USDT</Th>
                  <Th>Energy</Th>
                  <Th>Bandwidth</Th>
                  <Th>Last transaction</Th>
                  <Th>Status</Th>
                </tr>
              }
            >
              {(view?.data ?? []).map((row) => (
                <tr key={`${row.role}:${row.address}`}>
                  <Td>
                    <span class="font-medium">{row.label}</span>
                    <span class="block text-[11px] text-ink-muted">{ROLE_NOTE[row.role]}</span>
                  </Td>
                  <Td mono>
                    <Address value={row.address} />
                  </Td>
                  <Td numeric>{row.trx_sun === null ? <Blank title="Not read" /> : trx(row.trx_sun, 1)}</Td>
                  <Td numeric>{row.usdt === null ? <Blank title="Not read" /> : row.usdt.toFixed(2)}</Td>
                  <Td>
                    <Meter available={row.energy_available} limit={row.energy_limit} />
                  </Td>
                  <Td>
                    <Meter available={row.bandwidth_available} limit={row.bandwidth_limit} />
                  </Td>
                  <Td mono>
                    {row.last_txid === null ? (
                      row.read_failed.includes("last_transaction") ? (
                        <>
                          <Blank title="Last transaction not read" />
                          <span class="block font-sans text-[11px] text-ink-muted">last transaction not read</span>
                        </>
                      ) : (
                        <Blank title="No transaction found" />
                      )
                    ) : (
                      <>
                        {row.last_tx_at === null ? null : <span class="block">{utc(row.last_tx_at)}</span>}
                        <TxId value={row.last_txid} />
                      </>
                    )}
                  </Td>
                  <Td>
                    <Pill value={row.status} tone={STATUS_TONE[row.status]} />
                    {row.status === "low" && row.role === "activator" ? (
                      <span class="block text-[11px] text-ink-muted">below 20 TRX</span>
                    ) : null}
                    {/* The status reads the balance only; a missed last transaction is noted in its own cell. */}
                    {row.read_failed.some((part) => part !== "last_transaction") ? (
                      <span class="block text-[11px] text-ink-muted">
                        not read: {row.read_failed.filter((part) => part !== "last_transaction").join(", ")}
                      </span>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </Table>
            <div class="mt-2">
              <Note>
                Status: ok above the wallet's threshold; low below 20 TRX (activator); empty below 1 TRX. Chain data
                is cached {view?.cache_seconds ?? 60} s.
              </Note>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}
